import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Row, Col, Card, Pagination, Empty, Spin, Tag, Space, Typography, Statistic, Alert, Button, Select } from 'antd';
import { BankOutlined, FileTextOutlined, DollarOutlined, RightOutlined, UserOutlined, CalendarOutlined, QuestionCircleOutlined, InboxOutlined } from '@ant-design/icons';
import CurrencyFormat from 'react-currency-format';
import lib from '../../../assets/utils/lib';
import { notification } from 'antd';
import dayjs from 'dayjs';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../../../assets/utils/useAuth';
import BatchLoadingSkeleton from '../Batches/BatchLoadingSkeleton';
import SearchInput from '../SearchInput';
import {
  getClaimsArchiveScope,
  withClaimsArchiveScope,
  canManageClaimsArchive,
  buildClaimsArchiveRequest,
} from '../claimsArchiveHelpers';
import {
  getClaimsApprovedTotal,
  getPaApprovedTotal,
  getSubmittedTotal,
} from '../claimFinancialHelpers';
import ArchiveClaimsModal from '../ArchiveClaimsModal';
import StatusStripe, { lineCountsFromAggregate } from '../StatusStripe';
import './AdminMonthFacilitiesList.css';
import useClaimsRollout from '../../../hooks/useClaimsRollout';
import {
  DATE_AMOUNT_ITEM_SORT_OPTIONS,
  LATEST_SUBMISSION_SORT_OPTIONS,
  parseSortSelectValue,
  sortSelectValue,
  SORT_BY,
  SORT_DIR,
} from '../../../utils/tableSortParams';

const { Text, Title } = Typography;

/**
 * AdminMonthFacilitiesList - Displays facilities with claims in selected month (Admin)
 * Shows SUMMARY DATA ONLY - no claims loaded upfront
 * Second level of the simplified 3-level hierarchy
 */
const AdminMonthFacilitiesList = ({ month, onSelect }) => {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const canArchiveClaims = canManageClaimsArchive(user);
  const [archiveModalOpen, setArchiveModalOpen] = useState(false);
  const [archiveModalMode, setArchiveModalMode] = useState('archive');
  const [archiveRequest, setArchiveRequest] = useState(null);
  const FACILITIES_SEARCH_PARAM = 'adminFacilitiesSearch';
  const FACILITIES_PAGE_PARAM = 'adminFacilitiesPage';
  const FACILITIES_PAGE_SIZE_PARAM = 'adminFacilitiesPageSize';
  const [facilities, setFacilities] = useState([]);
  const [monthInfo, setMonthInfo] = useState(null);
  const [aggregates, setAggregates] = useState(null);
  const [searchMeta, setSearchMeta] = useState(null);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0,
  });
  // Spec §9 (new flow): facilities in a month default to newest claim
  // submission first, with Newest / Oldest submission in the sort menu. With
  // the flow off, the sort and its default are exactly as before.
  const { submit_separation_enabled: submissionSortEnabled } = useClaimsRollout();
  const sortOptions = submissionSortEnabled
    ? [...LATEST_SUBMISSION_SORT_OPTIONS, ...DATE_AMOUNT_ITEM_SORT_OPTIONS]
    : DATE_AMOUNT_ITEM_SORT_OPTIONS;
  const [listSort, setListSort] = useState(() => (
    submissionSortEnabled
      ? { sort_by: SORT_BY.LATEST_SUBMISSION, sort_dir: SORT_DIR.DESC }
      : { sort_by: SORT_BY.CHECKIN_DATE, sort_dir: SORT_DIR.DESC }
  ));
  const fetchingRef = useRef(false);
  const abortControllerRef = useRef(null);
  
  // Get search query from URL
  const searchQuery = searchParams.get(FACILITIES_SEARCH_PARAM) || '';
  const archiveScope = getClaimsArchiveScope(searchParams);

  const fetchFacilities = useCallback(async (page = 1, search = '') => {
    if (!month) return;

    // Get stable month key
    const monthKey = typeof month === 'string' ? month : (month.month || month.key || month);
    
    // Cancel any ongoing request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    
    // Prevent duplicate calls only if currently fetching
    if (fetchingRef.current) {
      console.log('Already fetching, skipping duplicate request');
      return;
    }
    
    fetchingRef.current = true;
    abortControllerRef.current = new AbortController();

    setLoading(true);
    try {
      console.log('Fetching facilities for month:', monthKey, 'page:', page, 'search:', search);
      const params = withClaimsArchiveScope({
        page,
        pageSize: pagination.pageSize,
        sort_by: listSort.sort_by,
        sort_dir: listSort.sort_dir,
      }, archiveScope);
      
      // Add search parameter if provided
      if (search && search.trim()) {
        params.search = search.trim();
      }
      
      const response = await lib.getAdminMonthFacilities(monthKey, params);

      if (response?.data?.error) {
        notification.error({
          message: 'Error',
          description: response.data.message || 'Failed to fetch facilities',
        });
        return;
      }

      // Backend returns: { error, message, data: { month, month_label, facilities: [...] }, aggregates: {...}, pagination: {...}, search: {...} }
      const responseData = response?.data || {};
      const data = responseData.data || {};
      const facilitiesList = Array.isArray(data.facilities) ? data.facilities : [];
      
      setFacilities(facilitiesList);
      setMonthInfo({
        month: data.month || monthKey,
        month_label: data.month_label,
      });
      setAggregates(responseData.aggregates || null);
      setSearchMeta(responseData.search || null); // Store search metadata
      setPagination((prev) => ({
        ...prev,
        current: responseData.pagination?.page || page,
        pageSize: responseData.pagination?.pageSize || prev.pageSize,
        total: responseData.pagination?.total || facilitiesList.length || 0,
      }));
    } catch (error) {
      console.error('Error fetching facilities:', error);
      notification.error({
        message: 'Error',
        description: error?.response?.data?.message || 'Failed to fetch facilities',
      });
    } finally {
      setLoading(false);
      fetchingRef.current = false;
      abortControllerRef.current = null;
    }
  }, [month, pagination.pageSize, archiveScope, listSort]); // eslint-disable-line react-hooks/exhaustive-deps -- searchQuery passed as arg

  // Fetch facilities when month changes
  useEffect(() => {
    if (!month) {
      setFacilities([]);
      setMonthInfo(null);
      return;
    }
    
    const monthKey = typeof month === 'string' ? month : (month.month || month.key || month);
    console.log('Month changed, fetching facilities for:', monthKey, 'month object:', month);
    
    // Reset pagination to page 1 when month changes
    setPagination((prev) => ({ ...prev, current: 1 }));
    
    // Reset fetching flag to allow new request
    fetchingRef.current = false;
    
    // Fetch facilities - inline to avoid stale closure
    const fetchNow = async () => {
      if (fetchingRef.current) {
        console.log('Already fetching, skipping');
        return;
      }
      fetchingRef.current = true;
      setLoading(true);
      
      try {
        console.log('Making API call for month:', monthKey);
        const params = {
          page: 1,
          pageSize: pagination.pageSize,
          sort_by: listSort.sort_by,
          sort_dir: listSort.sort_dir,
        };
        
        // Add search parameter if provided
        const urlSearch = searchParams.get(FACILITIES_SEARCH_PARAM) || '';
        if (urlSearch && urlSearch.trim()) {
          params.search = urlSearch.trim();
        }
        
        const response = await lib.getAdminMonthFacilities(monthKey, params);

        if (response?.data?.error) {
          notification.error({
            message: 'Error',
            description: response.data.message || 'Failed to fetch facilities',
          });
          return;
        }

        const responseData = response?.data || {};
        const data = responseData.data || {};
        const facilitiesList = Array.isArray(data.facilities) ? data.facilities : [];
        
        console.log('Received facilities:', facilitiesList.length);
        setFacilities(facilitiesList);
        setMonthInfo({
          month: data.month || monthKey,
          month_label: data.month_label,
        });
        setAggregates(responseData.aggregates || null);
        setSearchMeta(responseData.search || null); // Store search metadata
        setPagination((prev) => ({
          ...prev,
          current: responseData.pagination?.page || 1,
          pageSize: responseData.pagination?.pageSize || prev.pageSize,
          total: responseData.pagination?.total || facilitiesList.length || 0,
        }));
      } catch (error) {
        console.error('Error fetching facilities:', error);
        notification.error({
          message: 'Error',
          description: error?.response?.data?.message || 'Failed to fetch facilities',
        });
      } finally {
        setLoading(false);
        fetchingRef.current = false;
      }
    };
    
    fetchNow();
  }, [month, pagination.pageSize, searchParams]);

  // Handle pagination changes (when user clicks page number)
  useEffect(() => {
    if (month && pagination.current > 1) {
      fetchFacilities(pagination.current, searchQuery);
    }
  }, [pagination.current]); // eslint-disable-line react-hooks/exhaustive-deps

  const skipFacilitiesSortReload = useRef(true);
  useEffect(() => {
    if (skipFacilitiesSortReload.current) {
      skipFacilitiesSortReload.current = false;
      return;
    }
    if (!month) return;
    fetchingRef.current = false;
    fetchFacilities(1, searchQuery);
  }, [listSort.sort_by, listSort.sort_dir]); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle search and page changes from URL
  useEffect(() => {
    if (month) {
      const page = parseInt(searchParams.get(FACILITIES_PAGE_PARAM) || pagination.current.toString());
      const currentSearch = searchParams.get(FACILITIES_SEARCH_PARAM) || '';
      fetchFacilities(page, currentSearch);
    }
  }, [searchQuery, searchParams]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSearch = (query) => {
    // Search is handled via URL params in SearchInput component
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const handlePageChange = (page, pageSize) => {
    setPagination((prev) => ({
      ...prev,
      current: page,
      pageSize: pageSize || prev.pageSize,
    }));
  };

  const formatMonthLabel = (monthString) => {
    const date = dayjs(monthString + '-01');
    return date.format('MMMM YYYY');
  };

  const getMonthKey = () => {
    if (!month) return null;
    if (typeof month === 'string') return month;
    return month.month || month.key || null;
  };

  const openFacilityArchive = (facility, mode = 'archive') => {
    const monthKey = getMonthKey();
    if (!monthKey || !facility?.facility_id) return;
    setArchiveRequest(buildClaimsArchiveRequest({
      scope: 'facility',
      month: monthKey,
      facilityId: facility.facility_id,
    }));
    setArchiveModalMode(mode);
    setArchiveModalOpen(true);
  };

  /**
   * Month-wide: `{ scope: 'month', month }` — every facility with claims that
   * month, not just the ones on this page. This page is the one place where the
   * month itself is the unit of work, so it is the one place that offers it.
   */
  const openMonthArchive = (mode = 'archive') => {
    const monthKey = getMonthKey();
    if (!monthKey) return;
    setArchiveRequest(buildClaimsArchiveRequest({ scope: 'month', month: monthKey }));
    setArchiveModalMode(mode);
    setArchiveModalOpen(true);
  };

  if (!month) {
    return <Empty description="No month selected" />;
  }

  if (facilities.length === 0 && !loading) {
    return (
      <>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', flexWrap: 'wrap', gap: '16px' }}>
          <Title level={2} style={{ margin: 0 }}>
            Facilities - {monthInfo?.month_label || formatMonthLabel(typeof month === 'string' ? month : (month.month || month.key))}
          </Title>
          <Space>
            <Select
              value={sortSelectValue(listSort.sort_by, listSort.sort_dir)}
              options={sortOptions}
              onChange={(value) => setListSort(parseSortSelectValue(value))}
              style={{ minWidth: 170 }}
            />
            <SearchInput 
              placeholder="Search facilities (name, city, address)..." 
              onSearch={handleSearch}
              loading={loading}
              searchParamName={FACILITIES_SEARCH_PARAM}
              pageParamName={FACILITIES_PAGE_PARAM}
            />
          </Space>
        </div>
        <Empty
          description={searchQuery ? `No facilities found for "${searchQuery}"` : `No facilities found for ${monthInfo?.month_label || formatMonthLabel(typeof month === 'string' ? month : (month.month || month.key))}`}
          style={{ padding: '40px' }}
        />
      </>
    );
  }

  const monthLineCounts = lineCountsFromAggregate(aggregates);
  const monthLineTotal = monthLineCounts
    ? monthLineCounts.approved + monthLineCounts.pending + monthLineCounts.queried + monthLineCounts.rejected
    : 0;

  return (
    <div className="admin-month-facilities-list">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', flexWrap: 'wrap', gap: '16px' }}>
        <Title level={2} style={{ margin: 0 }}>
          Facilities - {monthInfo?.month_label || formatMonthLabel(typeof month === 'string' ? month : (month.month || month.key))}
        </Title>
        <Space>
          <Select
            value={sortSelectValue(listSort.sort_by, listSort.sort_dir)}
            options={sortOptions}
            onChange={(value) => setListSort(parseSortSelectValue(value))}
            style={{ minWidth: 170 }}
          />
          <SearchInput 
            placeholder="Search facilities (name, city, address)..." 
            onSearch={handleSearch}
            loading={loading}
            searchParamName={FACILITIES_SEARCH_PARAM}
            pageParamName={FACILITIES_PAGE_PARAM}
          />
          {canArchiveClaims ? (
            <Button
              icon={<InboxOutlined />}
              onClick={() => openMonthArchive(archiveScope === 'archived' ? 'unarchive' : 'archive')}
            >
              {archiveScope === 'archived' ? 'Restore all facilities' : 'Archive all facilities'}
            </Button>
          ) : null}
        </Space>
      </div>

      {/* Search Results Info */}
      {searchMeta && searchMeta.applied && (
        <Alert
          message={
            <Space>
              <span>Found <strong>{searchMeta.results_count || 0}</strong> result{searchMeta.results_count !== 1 ? 's' : ''} for</span>
              <Tag color="blue">"{searchMeta.query}"</Tag>
            </Space>
          }
          type="info"
          showIcon
          closable
          style={{ marginBottom: '24px' }}
          onClose={() => {
            const newParams = new URLSearchParams(searchParams);
            newParams.delete(FACILITIES_SEARCH_PARAM);
            newParams.set(FACILITIES_PAGE_PARAM, '1');
            window.history.replaceState({}, '', `${window.location.pathname}?${newParams.toString()}`);
            handleSearch('');
          }}
        />
      )}
      
      {/* Dashboard Aggregates Cards */}
      {aggregates && (
        <Row gutter={[16, 16]} style={{ marginBottom: '32px' }}>
          <Col xs={24} sm={12} md={8} lg={6}>
            <Card
              style={{
                height: '100%',
                background: 'linear-gradient(135deg, #e6f4ff 0%, #bae0ff 100%)',
                border: '2px solid #1890ff',
                borderRadius: '16px',
                boxShadow: '0 4px 16px rgba(24, 144, 255, 0.15)',
                overflow: 'hidden',
                position: 'relative',
              }}
              bodyStyle={{ padding: '20px' }}
            >
              <div style={{ position: 'absolute', top: 0, right: 0, width: '100px', height: '100px', background: 'radial-gradient(circle, rgba(24, 144, 255, 0.2) 0%, transparent 70%)', borderRadius: '50%', transform: 'translate(30%, -30%)', pointerEvents: 'none' }} />
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'relative', zIndex: 1 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <Text style={{ fontSize: '12px', fontWeight: 500, color: '#262626', display: 'block', marginBottom: '8px' }}>Total Enrollees</Text>
                  <Text style={{ fontSize: '26px', fontWeight: 600, color: '#0050b3', display: 'block', marginBottom: '4px' }}>{aggregates.total_unique_enrollees || 0}</Text>
                  <div style={{ height: '1px', background: 'rgba(24, 144, 255, 0.3)', marginTop: '8px' }} />
                </div>
                <div style={{ width: '48px', height: '48px', borderRadius: '12px', background: 'rgba(24, 144, 255, 0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '24px', color: '#1890ff', flexShrink: 0 }}>
                  <UserOutlined />
                </div>
              </div>
            </Card>
          </Col>
          <Col xs={24} sm={12} md={8} lg={6}>
            <Card
              style={{
                height: '100%',
                background: 'linear-gradient(135deg, #f6ffed 0%, #d9f7be 100%)',
                border: '2px solid #52c41a',
                borderRadius: '16px',
                boxShadow: '0 4px 16px rgba(82, 196, 26, 0.15)',
                overflow: 'hidden',
                position: 'relative',
              }}
              bodyStyle={{ padding: '20px' }}
            >
              <div style={{ position: 'absolute', top: 0, right: 0, width: '100px', height: '100px', background: 'radial-gradient(circle, rgba(82, 196, 26, 0.2) 0%, transparent 70%)', borderRadius: '50%', transform: 'translate(30%, -30%)', pointerEvents: 'none' }} />
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'relative', zIndex: 1 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <Text style={{ fontSize: '12px', fontWeight: 500, color: '#262626', display: 'block', marginBottom: '8px' }}>Total Facilities</Text>
                  <Text style={{ fontSize: '26px', fontWeight: 600, color: '#389e0d', display: 'block', marginBottom: '4px' }}>{aggregates.total_facilities || 0}</Text>
                  <div style={{ height: '1px', background: 'rgba(82, 196, 26, 0.3)', marginTop: '8px' }} />
                </div>
                <div style={{ width: '48px', height: '48px', borderRadius: '12px', background: 'rgba(82, 196, 26, 0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '24px', color: '#52c41a', flexShrink: 0 }}>
                  <BankOutlined />
                </div>
              </div>
            </Card>
          </Col>
          <Col xs={24} sm={12} md={8} lg={6}>
            <Card
              style={{
                height: '100%',
                background: 'linear-gradient(135deg, #f9f0ff 0%, #efdbff 100%)',
                border: '2px solid #722ed1',
                borderRadius: '16px',
                boxShadow: '0 4px 16px rgba(114, 46, 209, 0.15)',
                overflow: 'hidden',
                position: 'relative',
              }}
              bodyStyle={{ padding: '20px' }}
            >
              <div style={{ position: 'absolute', top: 0, right: 0, width: '100px', height: '100px', background: 'radial-gradient(circle, rgba(114, 46, 209, 0.2) 0%, transparent 70%)', borderRadius: '50%', transform: 'translate(30%, -30%)', pointerEvents: 'none' }} />
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'relative', zIndex: 1 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <Text style={{ fontSize: '12px', fontWeight: 500, color: '#262626', display: 'block', marginBottom: '8px' }}>Total Claims</Text>
                  <Text style={{ fontSize: '26px', fontWeight: 600, color: '#531dab', display: 'block', marginBottom: '4px' }}>{aggregates.total_claims || 0}</Text>
                  <div style={{ height: '1px', background: 'rgba(114, 46, 209, 0.3)', marginTop: '8px' }} />
                </div>
                <div style={{ width: '48px', height: '48px', borderRadius: '12px', background: 'rgba(114, 46, 209, 0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '24px', color: '#722ed1', flexShrink: 0 }}>
                  <FileTextOutlined />
                </div>
              </div>
            </Card>
          </Col>
          <Col xs={24} sm={12} md={8} lg={6}>
            <Card
              style={{
                height: '100%',
                background: 'linear-gradient(135deg, #fff7e6 0%, #ffe7ba 100%)',
                border: '2px solid #fa8c16',
                borderRadius: '16px',
                boxShadow: '0 4px 16px rgba(250, 140, 22, 0.15)',
                overflow: 'hidden',
                position: 'relative',
              }}
              bodyStyle={{ padding: '20px' }}
            >
              <div style={{ position: 'absolute', top: 0, right: 0, width: '100px', height: '100px', background: 'radial-gradient(circle, rgba(250, 140, 22, 0.2) 0%, transparent 70%)', borderRadius: '50%', transform: 'translate(30%, -30%)', pointerEvents: 'none' }} />
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'relative', zIndex: 1 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <Text style={{ fontSize: '12px', fontWeight: 500, color: '#262626', display: 'block', marginBottom: '8px' }}>Claims approved</Text>
                  <Text style={{ fontSize: '24px', fontWeight: 600, color: '#d46b08', display: 'block', marginBottom: '4px' }}>
                    <CurrencyFormat
                      value={getClaimsApprovedTotal(aggregates)}
                      displayType={'text'}
                      thousandSeparator={true}
                      prefix={'₦'}
                      decimalScale={2}
                    />
                  </Text>
                  <div style={{ height: '1px', background: 'rgba(250, 140, 22, 0.3)', marginTop: '8px' }} />
                </div>
                <div style={{ width: '48px', height: '48px', borderRadius: '12px', background: 'rgba(250, 140, 22, 0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '24px', color: '#fa8c16', flexShrink: 0 }}>
                  <DollarOutlined />
                </div>
              </div>
            </Card>
          </Col>
          <Col xs={24} sm={12} md={8} lg={6}>
            <Card
              style={{
                height: '100%',
                background: 'linear-gradient(135deg, #f0f5ff 0%, #d6e4ff 100%)',
                border: '2px solid #2f54eb',
                borderRadius: '16px',
                boxShadow: '0 4px 16px rgba(47, 84, 235, 0.15)',
                overflow: 'hidden',
                position: 'relative',
              }}
              bodyStyle={{ padding: '20px' }}
            >
              <div style={{ position: 'absolute', top: 0, right: 0, width: '100px', height: '100px', background: 'radial-gradient(circle, rgba(47, 84, 235, 0.2) 0%, transparent 70%)', borderRadius: '50%', transform: 'translate(30%, -30%)', pointerEvents: 'none' }} />
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'relative', zIndex: 1 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <Text style={{ fontSize: '12px', fontWeight: 500, color: '#262626', display: 'block', marginBottom: '8px' }}>Submitted to PA</Text>
                  <Text style={{ fontSize: '24px', fontWeight: 600, color: '#1d39c4', display: 'block', marginBottom: '4px' }}>
                    <CurrencyFormat
                      value={getSubmittedTotal(aggregates)}
                      displayType={'text'}
                      thousandSeparator={true}
                      prefix={'₦'}
                      decimalScale={2}
                    />
                  </Text>
                  <div style={{ height: '1px', background: 'rgba(47, 84, 235, 0.3)', marginTop: '8px' }} />
                </div>
                <div style={{ width: '48px', height: '48px', borderRadius: '12px', background: 'rgba(47, 84, 235, 0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '24px', color: '#2f54eb', flexShrink: 0 }}>
                  <DollarOutlined />
                </div>
              </div>
            </Card>
          </Col>
          <Col xs={24} sm={12} md={8} lg={6}>
            <Card
              style={{
                height: '100%',
                background: 'linear-gradient(135deg, #f9f0ff 0%, #efdbff 100%)',
                border: '2px solid #722ed1',
                borderRadius: '16px',
                boxShadow: '0 4px 16px rgba(114, 46, 209, 0.15)',
                overflow: 'hidden',
                position: 'relative',
              }}
              bodyStyle={{ padding: '20px' }}
            >
              <div style={{ position: 'absolute', top: 0, right: 0, width: '100px', height: '100px', background: 'radial-gradient(circle, rgba(114, 46, 209, 0.2) 0%, transparent 70%)', borderRadius: '50%', transform: 'translate(30%, -30%)', pointerEvents: 'none' }} />
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'relative', zIndex: 1 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <Text style={{ fontSize: '12px', fontWeight: 500, color: '#262626', display: 'block', marginBottom: '8px' }}>PA approved</Text>
                  <Text style={{ fontSize: '24px', fontWeight: 600, color: '#531dab', display: 'block', marginBottom: '4px' }}>
                    <CurrencyFormat
                      value={getPaApprovedTotal(aggregates)}
                      displayType={'text'}
                      thousandSeparator={true}
                      prefix={'₦'}
                      decimalScale={2}
                    />
                  </Text>
                  <div style={{ height: '1px', background: 'rgba(114, 46, 209, 0.3)', marginTop: '8px' }} />
                </div>
                <div style={{ width: '48px', height: '48px', borderRadius: '12px', background: 'rgba(114, 46, 209, 0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '24px', color: '#722ed1', flexShrink: 0 }}>
                  <DollarOutlined />
                </div>
              </div>
            </Card>
          </Col>
          {aggregates.average_claim_cost !== undefined && (
            <Col xs={24} sm={12} md={8} lg={6}>
              <Card
                style={{
                  height: '100%',
                  background: 'linear-gradient(135deg, #e6fffb 0%, #b5f5ec 100%)',
                  border: '2px solid #13c2c2',
                  borderRadius: '16px',
                  boxShadow: '0 4px 16px rgba(19, 194, 194, 0.15)',
                  overflow: 'hidden',
                  position: 'relative',
                }}
                bodyStyle={{ padding: '20px' }}
              >
                <div style={{ position: 'absolute', top: 0, right: 0, width: '100px', height: '100px', background: 'radial-gradient(circle, rgba(19, 194, 194, 0.2) 0%, transparent 70%)', borderRadius: '50%', transform: 'translate(30%, -30%)', pointerEvents: 'none' }} />
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'relative', zIndex: 1 }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <Text style={{ fontSize: '12px', fontWeight: 500, color: '#262626', display: 'block', marginBottom: '8px' }}>Avg Claim Cost</Text>
                    <Text style={{ fontSize: '24px', fontWeight: 600, color: '#08979c', display: 'block', marginBottom: '4px' }}>
                      <CurrencyFormat
                        value={aggregates.average_claim_cost || 0}
                        displayType={'text'}
                        thousandSeparator={true}
                        prefix={'₦'}
                        decimalScale={2}
                      />
                    </Text>
                    <div style={{ height: '1px', background: 'rgba(19, 194, 194, 0.3)', marginTop: '8px' }} />
                  </div>
                  <div style={{ width: '48px', height: '48px', borderRadius: '12px', background: 'rgba(19, 194, 194, 0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '24px', color: '#13c2c2', flexShrink: 0 }}>
                    <DollarOutlined />
                  </div>
                </div>
              </Card>
            </Col>
          )}
          {aggregates.average_claims_per_facility !== undefined && (
            <Col xs={24} sm={12} md={8} lg={6}>
              <Card
                style={{
                  height: '100%',
                  background: 'linear-gradient(135deg, #fff0f6 0%, #ffd6e7 100%)',
                  border: '2px solid #eb2f96',
                  borderRadius: '16px',
                  boxShadow: '0 4px 16px rgba(235, 47, 150, 0.15)',
                  overflow: 'hidden',
                  position: 'relative',
                }}
                bodyStyle={{ padding: '20px' }}
              >
                <div style={{ position: 'absolute', top: 0, right: 0, width: '100px', height: '100px', background: 'radial-gradient(circle, rgba(235, 47, 150, 0.2) 0%, transparent 70%)', borderRadius: '50%', transform: 'translate(30%, -30%)', pointerEvents: 'none' }} />
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'relative', zIndex: 1 }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <Text style={{ fontSize: '12px', fontWeight: 500, color: '#262626', display: 'block', marginBottom: '8px' }}>Avg Claims/Facility</Text>
                    <Text style={{ fontSize: '26px', fontWeight: 600, color: '#c41d7f', display: 'block', marginBottom: '4px' }}>{Number(aggregates.average_claims_per_facility || 0).toFixed(2)}</Text>
                    <div style={{ height: '1px', background: 'rgba(235, 47, 150, 0.3)', marginTop: '8px' }} />
                  </div>
                  <div style={{ width: '48px', height: '48px', borderRadius: '12px', background: 'rgba(235, 47, 150, 0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '24px', color: '#eb2f96', flexShrink: 0 }}>
                    <FileTextOutlined />
                  </div>
                </div>
              </Card>
            </Col>
          )}
          {aggregates.claims_with_queries !== undefined && (
            <Col xs={24} sm={12} md={8} lg={6}>
              <Card
                style={{
                  height: '100%',
                  background: 'linear-gradient(135deg, #fff1f0 0%, #ffccc7 100%)',
                  border: '2px solid #f5222d',
                  borderRadius: '16px',
                  boxShadow: '0 4px 16px rgba(245, 34, 45, 0.15)',
                  overflow: 'hidden',
                  position: 'relative',
                }}
                bodyStyle={{ padding: '20px' }}
              >
                <div style={{ position: 'absolute', top: 0, right: 0, width: '100px', height: '100px', background: 'radial-gradient(circle, rgba(245, 34, 45, 0.2) 0%, transparent 70%)', borderRadius: '50%', transform: 'translate(30%, -30%)', pointerEvents: 'none' }} />
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'relative', zIndex: 1 }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <Text style={{ fontSize: '12px', fontWeight: 500, color: '#262626', display: 'block', marginBottom: '8px' }}>Claims with Queries</Text>
                    <Text style={{ fontSize: '26px', fontWeight: 600, color: '#cf1322', display: 'block', marginBottom: '4px' }}>{aggregates.claims_with_queries || 0}</Text>
                    <div style={{ height: '1px', background: 'rgba(245, 34, 45, 0.3)', marginTop: '8px' }} />
                  </div>
                  <div style={{ width: '48px', height: '48px', borderRadius: '12px', background: 'rgba(245, 34, 45, 0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '24px', color: '#f5222d', flexShrink: 0 }}>
                    <QuestionCircleOutlined />
                  </div>
                </div>
              </Card>
            </Col>
          )}
          {aggregates.total_items_count !== undefined && (
            <Col xs={24} sm={12} md={8} lg={6}>
              <Card
                style={{
                  height: '100%',
                  background: 'linear-gradient(135deg, #f0f5ff 0%, #d6e4ff 100%)',
                  border: '2px solid #2f54eb',
                  borderRadius: '16px',
                  boxShadow: '0 4px 16px rgba(47, 84, 235, 0.15)',
                  overflow: 'hidden',
                  position: 'relative',
                }}
                bodyStyle={{ padding: '20px' }}
              >
                <div style={{ position: 'absolute', top: 0, right: 0, width: '100px', height: '100px', background: 'radial-gradient(circle, rgba(47, 84, 235, 0.2) 0%, transparent 70%)', borderRadius: '50%', transform: 'translate(30%, -30%)', pointerEvents: 'none' }} />
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'relative', zIndex: 1 }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <Text style={{ fontSize: '12px', fontWeight: 500, color: '#262626', display: 'block', marginBottom: '8px' }}>Total Items</Text>
                    <Text style={{ fontSize: '26px', fontWeight: 600, color: '#1d39c4', display: 'block', marginBottom: '4px' }}>{aggregates.total_items_count || 0}</Text>
                    <div style={{ height: '1px', background: 'rgba(47, 84, 235, 0.3)', marginTop: '8px' }} />
                  </div>
                  <div style={{ width: '48px', height: '48px', borderRadius: '12px', background: 'rgba(47, 84, 235, 0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '24px', color: '#2f54eb', flexShrink: 0 }}>
                    <FileTextOutlined />
                  </div>
                </div>
              </Card>
            </Col>
          )}
        </Row>
      )}

      {/* Visual Separator */}
      <div style={{ 
        marginBottom: '24px', 
        paddingTop: '24px',
        borderTop: '2px solid #e8e8e8',
        position: 'relative'
      }}>
        <Title level={4} style={{ margin: 0, marginBottom: '16px', color: '#262626' }}>
          Facilities
        </Title>
        {monthLineTotal > 0 && (
          <div style={{ marginBottom: '16px', maxWidth: 320 }}>
            <Text type="secondary" style={{ fontSize: '11px', display: 'block', marginBottom: '4px' }}>
              Line adjudication this month
            </Text>
            <StatusStripe counts={monthLineCounts} />
          </div>
        )}
      </div>

      <Row gutter={[16, 16]}>
        {facilities.map((facility) => (
          <Col xs={24} sm={12} md={8} lg={6} key={facility.facility_id}>
            <Card
              className="facility-summary-card"
              hoverable
              onClick={() => onSelect && onSelect(facility)}
              style={{
                borderRadius: '8px',
                cursor: 'pointer',
                transition: 'all 0.2s',
                border: '1px solid #f0f0f0',
              }}
            >
              <div className="facility-summary-card__content">
                <div className="facility-summary-card__header">
                  <BankOutlined style={{ marginRight: '8px', color: '#1890ff' }} />
                  <div style={{ flex: 1 }}>
                    <Title level={5} style={{ margin: 0, marginBottom: '6px', color: '#262626' }}>
                      {facility.facility_name}
                    </Title>
                    <div style={{ 
                      display: 'inline-block',
                      padding: '2px 8px',
                      borderRadius: '4px',
                      background: '#f5f5f5',
                      border: '1px solid #e8e8e8',
                      fontSize: '15px',
                      color: 'black',
                      fontWeight: 500,
                      marginTop: '4px',
                    }}>
                      {facility.facility_city}, {facility.facility_state}
                    </div>
                  </div>
                </div>
                
                <div className="facility-summary-card__stats">
                  <Space direction="vertical" size="small" style={{ width: '100%' }}>
                    {lineCountsFromAggregate(facility) && (
                      <StatusStripe counts={lineCountsFromAggregate(facility)} compact />
                    )}
                    <div>
                      <FileTextOutlined style={{ marginRight: '4px' }} />
                      <Tag color="blue">{facility.claims_count || 0}</Tag>
                      <Text type="secondary" style={{ fontSize: '12px' }}>
                        {facility.claims_count === 1 ? 'claim' : 'claims'}
                      </Text>
                    </div>
                    <div>
                      <UserOutlined style={{ marginRight: '4px' }} />
                      <Tag color="green">{facility.unique_patients || 0}</Tag>
                      <Text type="secondary" style={{ fontSize: '12px' }}>
                        {facility.unique_patients === 1 ? 'patient' : 'patients'}
                      </Text>
                    </div>
                    <div>
                      <DollarOutlined style={{ marginRight: '4px' }} />
                      <Space direction="vertical" size={0} style={{ display: 'inline-flex', verticalAlign: 'top' }}>
                        <Text type="secondary" style={{ fontSize: '11px' }}>
                          Submitted{' '}
                          <CurrencyFormat
                            value={getSubmittedTotal(facility)}
                            displayType={'text'}
                            thousandSeparator={true}
                            prefix={'₦'}
                            decimalScale={0}
                          />
                        </Text>
                        <Text type="secondary" style={{ fontSize: '11px' }}>
                          PA approved{' '}
                          <CurrencyFormat
                            value={getPaApprovedTotal(facility)}
                            displayType={'text'}
                            thousandSeparator={true}
                            prefix={'₦'}
                            decimalScale={0}
                          />
                        </Text>
                        <Text style={{ fontSize: '12px', fontWeight: 600 }}>
                          Claims approved{' '}
                          <CurrencyFormat
                            value={getClaimsApprovedTotal(facility)}
                            displayType={'text'}
                            thousandSeparator={true}
                            prefix={'₦'}
                            decimalScale={0}
                          />
                        </Text>
                      </Space>
                    </div>
                    {facility.last_claim_date && (
                      <div>
                        <CalendarOutlined style={{ marginRight: '4px' }} />
                        <Text type="secondary" style={{ fontSize: '11px' }}>
                          Last: {dayjs(facility.last_claim_date).format('MMM D, YYYY')}
                        </Text>
                      </div>
                    )}
                    {submissionSortEnabled && facility.latest_submission_at ? (
                      <div>
                        <Text type="secondary" style={{ fontSize: '11px' }}>
                          Submitted: {dayjs(facility.latest_submission_at).format('MMM D, YYYY h:mm A')}
                        </Text>
                      </div>
                    ) : null}
                  </Space>
                </div>

                {canArchiveClaims ? (
                  <div style={{ marginTop: 8 }} onClick={(e) => e.stopPropagation()}>
                    <Button
                      size="small"
                      block
                      onClick={() => openFacilityArchive(
                        facility,
                        archiveScope === 'archived' ? 'unarchive' : 'archive',
                      )}
                    >
                      {archiveScope === 'archived' ? 'Unarchive this month' : 'Archive this month'}
                    </Button>
                  </div>
                ) : null}

                <div className="facility-summary-card__action">
                  <RightOutlined />
                </div>
              </div>
            </Card>
          </Col>
        ))}
      </Row>

      {pagination.total > pagination.pageSize && (
        <div className="admin-month-facilities-list__pagination">
          <Pagination
            current={pagination.current}
            pageSize={pagination.pageSize}
            total={pagination.total}
            onChange={handlePageChange}
            showSizeChanger={false}
            showQuickJumper
            showTotal={(total, range) =>
              `${range[0]}-${range[1]} of ${total} facilities`
            }
          />
        </div>
      )}

      <ArchiveClaimsModal
        open={archiveModalOpen}
        mode={archiveModalMode}
        request={archiveRequest}
        allowMonthWide={canArchiveClaims}
        // This level of the admin hierarchy sits above the status split: a payment
        // stage is only chosen one level down (AdminFacilityStatusSummary), and the
        // `status` URL param is cleared on the way here. There is genuinely no
        // segment to carry, so it stays null and the dialog says "All statuses"
        // rather than labelling an all-status sweep as one queue.
        segment={null}
        // Facility count is scale, not a filter — a search narrows this list but
        // not the archive, so only pass it when the list is the whole month.
        monthWideFacilityCount={searchMeta?.applied ? null : pagination.total}
        onClose={() => {
          setArchiveModalOpen(false);
          setArchiveRequest(null);
        }}
        onSuccess={() => {
          setArchiveModalOpen(false);
          setArchiveRequest(null);
          fetchFacilities(pagination.current, searchQuery);
        }}
      />
    </div>
  );
};

export default AdminMonthFacilitiesList;

